import { useCallback, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { LtlBookingConfirmation } from '../../types/ltl';
import type { LtlFallbackOptions, LtlPusherService } from '../ltl-fallback-helpers';
import type { MutableRefObject } from 'react';

type FreightBookError = {
  Code: string;
  Message: string;
};

type FreightBookPusherData = {
  FreightShipmentId: number;
  ProductTransactionId: string | null;
  PickupTransactionId: string | null;
  BolNumber: string | null;
  ProNumber: string | null;
  ConfirmationNumber: string | null;
  Documents: unknown[];
  IsSuccess: boolean;
  Errors: FreightBookError[] | null;
  PersistenceWarning: string | null;
};

export type FreightBookPusherMessage = {
  messageGuid: string;
  uniqueMessageId: string;
  data: FreightBookPusherData;
};

export type UseFreightBookProgressOptions = {
  pusherService: LtlPusherService;
  pendingGuidsRef: MutableRefObject<Set<string>>;
  onConfirmation: (confirmation: LtlBookingConfirmation) => void;
  onError: (message: string, code?: string) => void;
  onComplete: () => void;
  timeoutMs?: number;
};

export type UseFreightBookProgressReturn = {
  startListening: (jobGuids: string[]) => void;
  cancelListening: () => void;
};

export const mapPascalCaseBookingConfirmation = (data: FreightBookPusherData): LtlBookingConfirmation => ({
  freightShipmentId: data.FreightShipmentId ?? 0,
  productTransactionId: data.ProductTransactionId ?? '',
  pickupTransactionId: data.PickupTransactionId ?? '',
  bolNumber: data.BolNumber ?? '',
  proNumber: data.ProNumber ?? '',
  pickupConfirmationNumber: data.ConfirmationNumber ?? '',
});

const messages = defineMessages({
  bookingFailed: {
    id: 'OwF/ta',
    defaultMessage: 'Booking failed. Please try again.',
    description: 'Error shown when the booking Pusher message indicates failure and no specific error message is present',
  },
  bookingTimedOut: {
    id: 'WiYVPj',
    defaultMessage: 'Booking request timed out. Please try again.',
    description: 'Error shown when the booking Pusher event times out',
  },
});

// Booking now waits on a backend document-availability poll (~12.5s) before the "freight-book"
// Pusher event fires, on top of normal carrier booking time — give it more headroom than quotes.
const DEFAULT_TIMEOUT_MS = 60000;

export const useFreightBookProgress = ({
  pusherService,
  pendingGuidsRef,
  onConfirmation,
  onError,
  onComplete,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: UseFreightBookProgressOptions): UseFreightBookProgressReturn => {
  const intl = useIntl();
  const onConfirmationRef = useRef(onConfirmation);
  onConfirmationRef.current = onConfirmation;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const handleMessage = useCallback(
    (message: FreightBookPusherMessage) => {
      const { messageGuid, data } = message;
      if (!pendingGuidsRef.current.has(messageGuid)) return;
      pendingGuidsRef.current.delete(messageGuid);

      if (!data.IsSuccess) {
        const errorCode = data.Errors?.[0]?.Code;
        const errorMessage = data.Errors?.[0]?.Message ?? intl.formatMessage(messages.bookingFailed);
        onErrorRef.current(errorMessage, errorCode);
      } else {
        onConfirmationRef.current(mapPascalCaseBookingConfirmation(data));
      }

      if (pendingGuidsRef.current.size === 0) {
        onCompleteRef.current();
      }
    },
    // pendingGuidsRef is a stable ref — .current is always fresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl],
  );

  useEffect(() => {
    const fallbackOptions: LtlFallbackOptions = {
      timeoutMs,
      onTimeout: () => {
        if (pendingGuidsRef.current.size === 0) return;
        pendingGuidsRef.current.clear();
        onErrorRef.current(intl.formatMessage(messages.bookingTimedOut));
        onCompleteRef.current();
      },
    };

    const unsubscribe = pusherService.subscribe<FreightBookPusherMessage>('freight-book', handleMessage, fallbackOptions);

    return unsubscribe;
    // pendingGuidsRef is a stable ref — .current is always fresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pusherService, timeoutMs, handleMessage]);

  const startListening = useCallback((jobGuids: string[]) => {
    pendingGuidsRef.current.clear();
    jobGuids.forEach((guid) => pendingGuidsRef.current.add(guid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelListening = useCallback(() => {
    pendingGuidsRef.current.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { startListening, cancelListening };
};
